import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { TrendingUp, ArrowLeft, Shield, Loader2, Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { BackgroundOrbs, GradientText, StaggerContainer, StaggerItem } from '@/components/ui/animations';

export default function Login({ auth }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Timeout to prevent infinite spinner — 15 seconds max
    const timeoutId = setTimeout(() => {
      setLoading(false);
      toast.error('Login is taking too long. Please try again.');
    }, 15000);
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { timeout: 12000 });
      
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setPendingUserId(response.data.user_id);
        toast.info('Please enter your 2FA code');
        setLoading(false);
        clearTimeout(timeoutId);
        return;
      }
      
      localStorage.setItem('token', response.data.access_token);
      await auth.checkAuth();
      clearTimeout(timeoutId);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.code === 'ECONNABORTED') {
        toast.error('Login timed out. Please check your connection and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login/2fa`, {
        user_id: pendingUserId,
        code: twoFACode
      });
      
      localStorage.setItem('token', response.data.access_token);
      await auth.checkAuth();
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || '2FA verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center relative overflow-hidden">
      {/* Animated Background */}
      <BackgroundOrbs />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-20" />
      
      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(6,182,212,0.1),transparent_50%)]" />

      <div className="relative z-10 w-full max-w-md px-6">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link 
            to="/" 
            className="inline-flex items-center text-slate-400 hover:text-white transition-colors mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
        </motion.div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="backdrop-blur-xl bg-slate-900/60 border border-white/10 rounded-2xl p-8 border border-white/10"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3 group mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-shadow">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
            </Link>
            <h1 className="text-3xl font-bold text-white mb-2">Welcome Back</h1>
            <p className="text-slate-400">Sign in to access your trading dashboard</p>
          </div>

          {!requires2FA ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <StaggerContainer>
                <StaggerItem>
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm font-medium">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="pl-10 h-12 bg-slate-900/50 border-white/10 text-white placeholder:text-slate-500 focus:border-blue-500/50 focus:ring-blue-500/20"
                        required
                        data-testid="login-email"
                      />
                    </div>
                  </div>
                </StaggerItem>

                <StaggerItem>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-300 text-sm font-medium">Password</Label>
                      <Link to="/forgot-password" className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="pl-10 pr-10 h-12 bg-slate-900/50 border-white/10 text-white placeholder:text-slate-500 focus:border-blue-500/50 focus:ring-blue-500/20"
                        required
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </StaggerItem>

                <StaggerItem>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
                    data-testid="login-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </StaggerItem>
              </StaggerContainer>
            </form>
          ) : (
            /* 2FA Form */
            <form onSubmit={handle2FASubmit} className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Two-Factor Authentication</h2>
                <p className="text-slate-400 text-sm">Enter the 6-digit code from your authenticator app</p>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300 text-sm font-medium">Verification Code</Label>
                <Input
                  type="text"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="h-14 text-center text-2xl font-mono tracking-widest bg-slate-900/50 border-white/10 text-white placeholder:text-slate-500 focus:border-blue-500/50"
                  maxLength={6}
                  required
                  autoFocus
                  data-testid="2fa-code"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setRequires2FA(false);
                    setTwoFACode('');
                  }}
                  className="flex-1 h-12 border-white/10 text-slate-300 hover:bg-white/5"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading || twoFACode.length !== 6}
                  className="flex-1 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  data-testid="2fa-submit"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify'}
                </Button>
              </div>
            </form>
          )}

          {/* Register Link */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-slate-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                Create account
              </Link>
            </p>
          </div>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 flex items-center justify-center gap-6 text-slate-500"
        >
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span className="text-xs">256-bit Encryption</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            <span className="text-xs">Secure Login</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
